/**
 * Mission shell: top bar (code, name, status, PLAN · LIVE · REPLAY, secondary nav, connection +
 * clock sync, UTC/local clocks) and, on phones, a compact top bar + bottom tab bar.
 * Loads the mission store for `:id` and renders the feature page in <Outlet/>.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import type { Mission } from '@apos/shared';
import {
  Button,
  Chip,
  cx,
  EmptyState,
  Icon,
  IconButton,
  MenuItem,
  Modal,
  Popover,
  Segmented,
  Select,
  toast,
  Toggle,
  type IconName,
} from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useClockEstimate, useServerNow } from '../lib/clock';
import { fmt } from '../lib/fmt';
import { notificationPermission, requestNotificationPermission } from '../lib/notifications';
import { usePrefs, type Basemap } from '../lib/prefs';
import type { SocketStatus } from '../lib/ws';
import { useMission } from '../state/mission';
import { AlertLayer } from '../features/alerts/AlertLayer';
import { LocationHelpHost, LocationIndicator, useAutoShareLocation } from '../features/live/SharingPill';
import { ShareLocationSetting } from '../components/ShareLocationSetting';
import { SpokenUpdatesSetting } from '../components/SpokenUpdatesSetting';
import { useSpokenUpdates } from '../features/callouts/useSpokenUpdates';
import { CommsHost, CommsLauncher, CommsMenuTile, useUnread } from '../features/comms';
import { useSession } from '../state/session';
import { InviteCrew } from '../features/setup/CrewPanel';
import { useIsPhone } from './useMediaQuery';
import { hasReplayData, canReplayShoot } from '../features/replay/availability';
import { atLeast, STATUS_LABEL, STATUS_TONE } from './labels';
import { Splash } from './Splash';

export type MissionMode = 'plan' | 'live' | 'replay';

const SECONDARY: Array<{ to: string; label: string; icon: IconName }> = [
  { to: 'point', label: 'Point', icon: 'crosshair' },
  { to: 'timecode', label: 'Timecode', icon: 'timecode' },
];

/** Phone "More" sheet: everything that isn't a field tab. */
const PHONE_MORE: Array<{ to: string; label: string; icon: IconName }> = [
  { to: 'replay', label: 'Replay', icon: 'replay' },
  { to: 'timecode', label: 'Timecode', icon: 'timecode' },
];

/** Shoot tools without a top-bar or tab-bar home: shown as tiles (phone) / the Tools group (desktop). */
const MORE_TOOLS: Array<{ to: string; label: string; icon: IconName }> = [
  { to: 'a2a', label: 'Air-to-air', icon: 'a2a' },
  { to: 'media', label: 'Media', icon: 'media' },
];

export function MissionShell() {
  const { id } = useParams();
  const load = useMission((s) => s.load);
  const unload = useMission((s) => s.unload);
  const status = useMission((s) => s.status);
  const error = useMission((s) => s.error);
  const bundle = useMission((s) => s.bundle);
  const isPhone = useIsPhone();
  // My location is shared automatically while this mission is live (Settings switch to opt out).
  useAutoShareLocation();
  // Spoken updates the server sends me (the Airview Studio app speaks them itself when it can).
  useSpokenUpdates();

  useEffect(() => {
    if (id) void load(id);
  }, [id, load]);
  useEffect(() => () => unload(), [unload]);

  if (!bundle || bundle.mission.id !== id) {
    if (status === 'error') {
      return (
        <div className="page page--center">
          <EmptyState
            icon="alert"
            title="Shoot unavailable"
            body={error ?? 'You may not be a member of this shoot.'}
            action={
              <Link className="btn btn--secondary btn--md" to="/missions">
                <span className="btn__label">Back to shoots</span>
              </Link>
            }
          />
        </div>
      );
    }
    return <Splash />;
  }

  if (bundle.mission.archivedAt != null && window.location.pathname.endsWith('/live')) {
    if (status !== 'ready') return <Splash />;
    return <Navigate to={`/m/${bundle.mission.id}/${canReplayShoot(bundle.mission.status, useMission.getState().record) ? 'replay' : 'plan'}`} replace />;
  }

  return (
    <div className={cx('shell', isPhone ? 'shell--phone' : 'shell--desktop')}>
      {isPhone ? <PhoneTopBar mission={bundle.mission} /> : <DesktopTopBar mission={bundle.mission} />}
      {bundle.mission.archivedAt != null ? <ArchivedMissionBanner /> : null}
      <div className="shell__content">
        <Outlet key={bundle.mission.id} />
      </div>
      {isPhone ? <PhoneTabBar /> : null}
      <AlertLayer />
      <CommsHost />
      <LocationHelpHost />
    </div>
  );
}

function ArchivedMissionBanner() {
  const bundle = useMission(s => s.bundle)!;
  const [busy, setBusy] = useState(false);
  return <div className="mission-archive-banner" role="status"><span>Archived · read only. Recorded history is preserved.</span><Link to="/missions?view=archived">Archived shoots</Link>{bundle.myRole === 'owner' ? <Button size="sm" disabled={busy} onClick={async () => { setBusy(true); try { const mission = await api.missions.restore(bundle.mission.id); const current = useMission.getState().bundle; if (current?.mission.id === mission.id) useMission.getState().setBundle({ ...current, mission }); toast({ title: 'Shoot restored', tone: 'success' }); } catch (e) { toast({ title: 'Could not restore shoot', body: errorMessage(e), tone: 'alert' }); } finally { setBusy(false); } }}>Restore shoot</Button> : null}</div>;
}

/** `/m/:id` → the most useful mode for the mission's status. */
export function MissionIndexRedirect() {
  const bundle = useMission((s) => s.bundle);
  const ready = useMission((s) => s.status === "ready");
  const replayAvailable = useMission((s) => hasReplayData(s.record));
  if (!bundle || !ready) return <Splash inline />;
  const s = bundle.mission.status;
  return <Navigate to={s === 'live' ? 'live' : s === 'ended' && replayAvailable ? 'replay' : 'plan'} replace />;
}

function useCurrentMode(): string {
  const loc = useLocation();
  const seg = loc.pathname.split('/')[3] ?? '';
  return seg;
}

// ───────────── Desktop ─────────────

function DesktopTopBar({ mission }: { mission: Mission }) {
  const mode = useCurrentMode();
  const replayAvailable = useMission((s) => s.status === 'ready' && canReplayShoot(s.bundle?.mission.status,s.record));
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  return (
    <header className="topbar">
      <div className="topbar__left">
        <Link to="/missions" className="topbar__back" aria-label="All shoots" title="All shoots">
          <Icon name="chevron-left" size={18} />
        </Link>
        <MissionTitle mission={mission} />
      </div>
      <nav className="topbar__center" aria-label="Mission mode">
        <Segmented<MissionMode | ''>
          size="lg"
          label="Mode"
          className="mode-switch"
          value={(['plan', 'live', 'replay'] as const).includes(mode as MissionMode) ? (mode as MissionMode) : ''}
          onChange={(v) => v && nav(v, { replace: true })}
          options={[
            { value: 'plan', label: 'Plan' },
            { value: 'live', label: 'Live', title: mission.status === 'live' ? 'Shoot is live' : 'Live view' },
            { value: 'replay', label: 'Replay', disabled: !replayAvailable, title: !replayAvailable ? 'Replay needs a live or ended shoot with recorded data' : 'Replay recorded shoot' },
          ]}
        />
        <div className="subnav">
          {SECONDARY.map((s) => (
            <NavLink key={s.to} to={s.to} replace title={s.label} className={({ isActive }) => cx('subnav__link', isActive && 'is-on')}>
              <Icon name={s.icon} size={15} />
              <span className="subnav__label">{s.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
      <div className="topbar__right">
        <CommsLauncher />
        <div className="topbar__status">
          <LocationIndicator />
          <ConnectionIndicator />
          <span className="topbar__status-sep" aria-hidden />
          <Clocks timezone={mission.timezone} />
        </div>
        <IconButton ref={anchor} icon="more-v" label="Mission menu" onClick={() => setMenuOpen((v) => !v)} />
        <Popover open={menuOpen} onClose={() => { if (!document.querySelector('.modal-root')) setMenuOpen(false); }} anchor={anchor} className="popover--wide">
          <MissionMenu onDone={() => setMenuOpen(false)} />
        </Popover>
      </div>
    </header>
  );
}

function MissionTitle({ mission, compact }: { mission: Mission; compact?: boolean }) {
  return (
    <div className={cx('mission-title', compact && 'mission-title--compact')}>
      <span className="mission-title__identity">
        <span className="mission-title__name" title={mission.name}>
          {mission.name}
        </span>
        <span className="mission-title__code num">{mission.code}</span>
      </span>
      <Chip tone={STATUS_TONE[mission.status]} dot={mission.status === 'live'} size="sm">
        {STATUS_LABEL[mission.status]}
      </Chip>
      {mission.simulation ? (
        <Chip size="sm" icon="sim" title={`Simulation · ${mission.simulation.scenarioId}`}>
          SIM
        </Chip>
      ) : null}
    </div>
  );
}

const CONN_TEXT: Record<SocketStatus, string> = {
  idle: 'IDLE',
  connecting: 'CONNECTING',
  open: 'SYNC',
  reconnecting: 'RECONNECTING',
  offline: 'OFFLINE',
  closed: 'CLOSED',
};

export function ConnectionIndicator({ compact }: { compact?: boolean }) {
  const conn = useMission((s) => s.connection);
  const reconnect = useMission((s) => s.reconnect);
  const est = useClockEstimate();
  const tone = conn === 'open' ? 'live' : conn === 'offline' || conn === 'closed' ? 'lost' : 'aging';
  const title =
    conn === 'open' && est
      ? `Connected · clock offset ${fmt.offset(est.offsetMs)} ± ${Math.round(est.uncertaintyMs)} ms (RTT ${Math.round(est.rttMs)} ms, ${est.sampleCount} samples)`
      : `Realtime ${CONN_TEXT[conn].toLowerCase()} — tap to reconnect`;
  return (
    <button type="button" className={cx('conn', `conn--${tone}`, compact && 'conn--compact')} title={title} aria-label={title} onClick={reconnect}>
      <span className="conn__dot" aria-hidden />
      {!compact ? (
        <span className="conn__text num">
          {conn === 'open' && est ? (
            <>
              <span className="conn__label">SYNC</span> ±{Math.max(1, Math.round(est.uncertaintyMs))} ms
            </>
          ) : (
            <span className="conn__label">{CONN_TEXT[conn]}</span>
          )}
        </span>
      ) : null}
    </button>
  );
}

export function Clocks({ timezone, compact }: { timezone: string; compact?: boolean }) {
  const now = useServerNow(1000);
  if (compact) {
    return (
      <div className="clocks clocks--compact num" title={fmt.local(now, timezone)}>
        {fmt.utc(now)}
      </div>
    );
  }
  return (
    <div className="clocks">
      <div className="clocks__row">
        <span className="clocks__tz">UTC</span>
        <ClockFigure text={fmt.utc(now).replace(/Z$/, '')} />
      </div>
      <div className="clocks__row clocks__row--local">
        <span className="clocks__tz">LCL</span>
        <ClockFigure text={fmt.local(now, timezone)} />
      </div>
    </div>
  );
}

/** HH:MM bright, :SS and any zone suffix dimmed — reads like an instrument, not a string. */
function ClockFigure({ text }: { text: string }) {
  const m = /^(\d{1,2}:\d{2})(:\d{2})?(.*)$/.exec(text);
  if (!m) return <span className="num">{text}</span>;
  return (
    <span className="num clocks__time">
      {m[1]}
      {m[2] ? <span className="clocks__sec">{m[2]}</span> : null}
      {m[3] ? <span className="clocks__zone">{m[3]}</span> : null}
    </span>
  );
}

// ───────────── Phone ─────────────

function PhoneTopBar({ mission }: { mission: Mission }) {
  return (
    <header className="topbar topbar--phone">
      <Link to="/missions" className="topbar__back" aria-label="All shoots">
        <Icon name="chevron-left" size={18} />
      </Link>
      <MissionTitle mission={mission} compact />
      <div className="topbar__right">
        <Clocks timezone={mission.timezone} compact />
        <ConnectionIndicator compact />
      </div>
    </header>
  );
}

function PhoneTabBar() {
  const [more, setMore] = useState(false);
  const mode = useCurrentMode();
  // Field-first: the four things you use around a shoot, everything else under More.
  // Alerts live on the map (bell → /alerts), so the alert list counts as the Map tab.
  const tabs: Array<{ to: string; label: string; icon: IconName; also?: string }> = [
    { to: 'live', label: 'Map', icon: 'map', also: 'alerts' },
    { to: 'point', label: 'Point', icon: 'crosshair' },
    { to: 'team', label: 'Team', icon: 'users' },
    // the day's schedule, shot list and setup
    { to: 'plan', label: 'Plan', icon: 'list' },
  ];
  const unread = useUnread();
  const moreActive = mode === 'a2a' || mode === 'media' || mode === 'replay' || mode === 'timecode';
  return (
    <>
      <nav className="tabbar" aria-label="Mission">
        {tabs.map((t) => (
          <NavLink key={t.to} to={t.to} replace className={({ isActive }) => cx('tabbar__tab', !more && (isActive || mode === t.also) && 'is-on')}>
            <span className="tabbar__icon" aria-hidden>
              <Icon name={t.icon} size={22} />
            </span>
            <span className="tabbar__label">{t.label}</span>
          </NavLink>
        ))}
        <button type="button" className={cx('tabbar__tab', (more || moreActive) && 'is-on')} onClick={() => setMore(true)} aria-expanded={more}>
          <span className="tabbar__icon" aria-hidden>
            <Icon name="more" size={22} />
          </span>
          <span className="tabbar__label">More</span>
          {unread ? <span className="comms-launch__badge num tabbar__badge" aria-label={`${unread} unread crew messages`}>{unread > 99 ? '99+' : unread}</span> : null}
        </button>
      </nav>
      <Modal open={more} onClose={() => setMore(false)} title="More" size="sm" testId="more-menu">
        <MissionMenu onDone={() => setMore(false)} phone />
      </Modal>
    </>
  );
}

// ───────────── Menu (shared) ─────────────

function MissionMenu({ onDone, phone }: { onDone(): void; phone?: boolean }) {
  const nav = useNavigate();
  const bundle = useMission((s) => s.bundle)!;
  const setBundle = useMission((s) => s.setBundle);
  const logout = useSession((s) => s.logout);
  const prefs = usePrefs();
  const [perm, setPerm] = useState(notificationPermission());
  const [invite, setInvite] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const replayAvailable = useMission((s) => s.status === 'ready' && canReplayShoot(s.bundle?.mission.status,s.record));
  const m = bundle.mission;
  const canProduce = m.archivedAt == null && atLeast(bundle.myRole, 'producer');

  // switching sections inside a shoot replaces history, so back / swipe-back leaves the shoot
  // instead of stepping through every tab you visited
  const go = (to: string) => {
    onDone();
    nav(to, { replace: !to.startsWith('/') });
  };

  const start = async () => {
    try {
      const mission = await api.missions.start(m.id);
      setBundle({ ...bundle, mission });
      toast({ title: 'Shoot live', body: mission.name, tone: 'success' });
      go('live');
    } catch (e) {
      toast({ title: 'Could not start shoot', body: errorMessage(e), tone: 'alert' });
    }
  };

  const tile = (s: { to: string; label: string; icon: IconName }) => (
    <button key={s.to} type="button" className="menu-tile" disabled={s.to === 'replay' && !replayAvailable} title={s.to === 'replay' && !replayAvailable ? 'Replay needs a live or ended shoot with recorded data' : undefined} onClick={() => go(s.to)}>
      <Icon name={s.icon} size={22} />
      <span>{s.label}</span>
    </button>
  );
  const canEnd = canProduce && m.status === 'live';
  const canArchive = bundle.myRole === 'owner' && m.archivedAt == null && m.status !== 'live';

  return (
    <div className={cx('mission-menu', phone && 'mission-menu--phone')}>
      <div className="menu-label mission-menu__head">
        <div className="mission-menu__titles">
          <div className="eyebrow num">{m.code}</div>
          <div className="menu-user">{m.name}</div>
        </div>
        <Chip tone={STATUS_TONE[m.status]} dot={m.status === 'live'} size="sm">
          {STATUS_LABEL[m.status]}
        </Chip>
      </div>
      {phone ? (
        // Navigate + tools: everything that isn't a tab, one tap away (no folded "More tools")
        <div className="menu-group menu-group--nav">
          <div className="menu-grid">
            {PHONE_MORE.map(tile)}
            {/* CommsMenuTile is owned by features/comms */}
            <CommsMenuTile onOpen={onDone} />
            {MORE_TOOLS.map(tile)}
          </div>
        </div>
      ) : (
        <div className="menu-group" role="group" aria-label="Tools">
          <div className="menu-group__label">Tools</div>
          {MORE_TOOLS.map((t) => (
            <MenuItem key={t.to} icon={t.icon} onClick={() => go(t.to)}>
              {t.label}
            </MenuItem>
          ))}
        </div>
      )}
      {canProduce ? (
        <div className="menu-group" role="group" aria-label="Shoot">
          <div className="menu-group__label">Shoot</div>
          {m.status === 'planned' ? (
            <MenuItem icon="play" onClick={start}>
              Start shoot
            </MenuItem>
          ) : null}
          <MenuItem icon="edit" onClick={() => go('plan?view=setup#mission-details')}>Edit shoot details</MenuItem>
          <MenuItem icon="users" onClick={() => setInvite(true)}>
            Invite crew…
          </MenuItem>
        </div>
      ) : null}
      <div className="menu-group" role="group" aria-label="Export">
        <div className="menu-group__label">Export</div>
        <MenuItem icon="download" onClick={() => window.open(api.exportUrl(m.id), '_blank')}>
          Export record (JSON)
        </MenuItem>
        <MenuItem icon="download" onClick={() => window.open(api.exportKmlUrl(m.id), '_blank')}>
          Export tracks (KML)
        </MenuItem>
      </div>
      <div className="menu-group" role="group" aria-label="App">
        <div className="menu-group__label">App</div>
        <MenuItem icon="team" onClick={() => go('/organization')}>Organization</MenuItem>
        <MenuItem icon="aircraft" onClick={() => go('/fleets')}>Fleets</MenuItem>
        <MenuItem icon="camera" onClick={() => go(`/cameras?airport=${encodeURIComponent(bundle.airports[0]?.icao??'KCVO')}&mission=${encodeURIComponent(m.id)}`)}>Cameras & ATC</MenuItem>
        <MenuItem icon="target" onClick={() => go(`/shot-planner?airport=${encodeURIComponent(bundle.airports[0]?.icao??'KCVO')}&mission=${encodeURIComponent(m.id)}`)}>Shot planner</MenuItem>
        <MenuItem icon="settings" onClick={() => go('/settings')}>My settings</MenuItem>
      </div>
      <div className="menu-group" role="group" aria-label="Preferences">
        <div className="menu-group__label">Preferences</div>
        <div className="menu-prefs">
          <div className="menu-pref">
            <span>Theme</span>
            <Segmented
              size="sm"
              value={prefs.theme}
              onChange={(v) => prefs.set('theme', v)}
              options={[
                { value: 'dark', label: 'Dark', icon: 'moon' },
                { value: 'daylight', label: 'Daylight', icon: 'sun' },
              ]}
            />
          </div>
          <div className="menu-pref">
            <span>Basemap</span>
            <Select value={prefs.basemap} onChange={(e) => prefs.set('basemap', e.target.value as Basemap)} aria-label="Basemap">
              <option value="dark">Dark</option>
              <option value="daylight">Daylight</option>
              <option value="satellite">Imagery (US only)</option>
            </Select>
          </div>
          <ShareLocationSetting />
          <SpokenUpdatesSetting />
          <Toggle checked={prefs.muteAlerts} onChange={(v) => prefs.set('muteAlerts', v)} label="Mute alert sounds and notifications (all my devices)" />
          {perm === 'default' ? (
            <Button size="sm" icon="bell" onClick={async () => setPerm(await requestNotificationPermission())}>
              Enable alert notifications
            </Button>
          ) : perm === 'denied' ? (
            <p className="menu-note">Alert notifications are blocked in this browser. Allow them in the site settings to get heads-ups here.</p>
          ) : null}
        </div>
      </div>
      <div className="menu-group" role="group" aria-label="Account">
        <MenuItem
          icon="logout"
          onClick={async () => {
            onDone();
            await logout();
            nav('/login');
          }}
        >
          Sign out
        </MenuItem>
      </div>
      {canEnd || canArchive ? (
        <div className="menu-group menu-group--danger" role="group" aria-label="End or archive">
          {canEnd ? (
            <MenuItem icon="stop" danger onClick={() => setConfirmEnd(true)}>
              End shoot…
            </MenuItem>
          ) : null}
          {canArchive ? (
            <MenuItem icon="archive" danger onClick={() => setConfirmArchive(true)}>
              Archive shoot…
            </MenuItem>
          ) : null}
        </div>
      ) : null}
      <Modal open={confirmArchive} onClose={() => !archiveBusy && setConfirmArchive(false)} eyebrow={m.code} title="Archive shoot?" size="sm" locked={archiveBusy} footer={<><Button variant="ghost" disabled={archiveBusy} onClick={() => setConfirmArchive(false)}>Cancel</Button><Button disabled={archiveBusy} onClick={async () => { setArchiveBusy(true); try { await api.missions.archive(m.id); setConfirmArchive(false); go('/missions?view=archived'); } catch (e) { toast({ title: 'Could not archive shoot', body: errorMessage(e), tone: 'alert' }); } finally { setArchiveBusy(false); } }}>Archive shoot</Button></>}><p>This removes the shoot from active lists. All tracks, marks, media and planning history stay available in Archived shoots. An owner can restore it.</p></Modal>
      <Modal open={invite} onClose={() => setInvite(false)} size="sm" eyebrow="Invite" title="Invite to shoot">
        <InviteCrew bundle={bundle} />
      </Modal>
      <Modal
        open={confirmEnd}
        onClose={() => setConfirmEnd(false)}
        size="sm"
        eyebrow={m.code}
        title="End shoot?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmEnd(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                try {
                  const mission = await api.missions.end(m.id);
                  setBundle({ ...bundle, mission });
                  setConfirmEnd(false);
                  go(hasReplayData(useMission.getState().record) ? 'replay' : 'plan');
                } catch (e) {
                  toast({ title: 'Could not end shoot', body: errorMessage(e), tone: 'alert' });
                }
              }}
            >
              End shoot
            </Button>
          </>
        }
      >
        <p className="muted">Ends live tracking, stops everyone's location sharing and opens the shoot in Replay.</p>
      </Modal>
    </div>
  );
}
